import $ from 'jquery';

import RailUtils from 'chairisher/util/rail';

import { trackArrowClick } from 'chairisher/analytics/components/rail';

/**
 * A component that turns an element into a RepeatingScroller, which cycles through the elements
 * in either direction infinitely.
 *
 * @param {Object=} settings The object containing the settings for the scroller
 * @param {AnalyticsUtils.Tracking.POSITION=} settings.analyticsTrackingPosition The position of the scroller
 * @param {AnalyticsUtils.Tracking.SUB_TYPE=} settings.analyticsTrackingSubtype The sub-type of the scroller
 * @param {AnalyticsUtils.Tracking.TYPE=} settings.analyticsTrackingType The type of the scroller
 * @param {Element} settings.container The outermost element that scrolls
 * @param {Array.<Element>} settings.children Collection of child elements displayed in the scroller
 * @param {boolean=} settings.childrenHaveImages Flag indicating whether every child's primary content is an image. This value is determines how controls are vertically aligned
 * @param {Element} settings.controllerContainerWrapperEl Optional element to place the controller container in for styling purposes
 * @param {boolean=} settings.shouldAutoScroll Flag indicating if the scroller should scroll on its own
 * @param {number=} settings.slideDurationInMs How long to wait before auto-advancing (ms)
 * @param {number=} settings.transitionDurationInMs How long to animate scroll transitions (ms)
 */
class RepeatingScroller {
    constructor(settings = {}) {
        this.analyticsTrackingPosition = settings.analyticsTrackingPosition ?? null;
        this.analyticsTrackingSubtype = settings.analyticsTrackingSubtype ?? null;
        this.analyticsTrackingType = settings.analyticsTrackingType ?? null;
        this.containerEl = settings.container ?? null;
        this.controllerContainerWrapperEl = settings.controllerContainerWrapperEl ?? null;
        this.children = settings.children ?? null;
        this.childrenHaveImages = settings.childrenHaveImages ?? true;
        this.shouldAutoScroll = settings.shouldAutoScroll ?? true;
        this.slideDurationInMs = settings.slideDurationInMs ?? 3000;
        this.transitionDurationInMs = settings.transitionDurationInMs ?? 400; // jQuery.animate's default

        this.$containerEl = $(this.containerEl); // jQuery instance of `this.containerEl` to provide for easier binding
        this.$windowEl = $(window); // jQuery instance of `window` to provide for easier binding

        this.autoScrollId = null; // Id of the auto scroll interval loop
        this.childWidth = 0; // The width of a child element; assumes all children are the same width
        this.controlContainerEl = null; // Optional Element containing directional arrows
        this.currentScrollLeft = 0; // The container element' (`this.containerEl`) current scrollLeft position
        this.eventNameSpace = 'repeatingscroller'; // The namespace under which events should be bound
        this.isAnimating = false; // Flag used to prevent multiple simultaneous animations
        this.isInitialized = false; // Flag used to determine if all of the elements have been initialized and are ready for use
        this.isRepositioning = false; // Flag that indicates the scroller is in the process of resetting everything
        this.midpoint = 0; // The index number that is the midpoint in the collection of children. Used to populate elements before AND after the first element on load
        this.minScrollCount = 3; // Minimum items to enable scroll
        this.numVisibleElements = 8; // The number of elements that should always be visible
        this.originX = 0; // The X coordinate that indicates the initial X coordinate that serves as the origin
    }

    /**
     * Initializes and binds all DOM events and sets up the scroller.
     */
    bind() {
        this.$containerEl.addClass('is-scroller');
        this.buildControls();
        this.resizeAction();
        this.isInitialized = true;

        if (this.numVisibleElements < this.children.length && this.childWidth < this.$containerEl.width()) {
            this.shouldAutoScroll = false;
        }

        this.$containerEl.on(this.nameSpacedEvent('scroll'), () => this.scrollAction());
        this.$windowEl.on(this.nameSpacedEvent('resize'), () => this.resizeAction());

        if (this.shouldAutoScroll) {
            this.autoscrollStart();
        }
    }

    /**
     * @returns {boolean} True indicates the scroller is autoscrolling
     */
    isAutoScrolling() {
        return !!this.autoScrollId;
    }

    /**
     * Scrolls the container backward by n element indexes
     *
     * @param {number} num The number of indexes to scroll backward
     */
    scrollBackward(num = 1) {
        this.scrollContainer(this.childWidth * -num);
    }

    /**
     * Scrolls the container forward by n element indexes
     *
     * @param {number} num The number of indexes to scroll forward
     */
    scrollForward(num = 1) {
        this.scrollContainer(this.childWidth * num);
    }

    /**
     * Stops the scroller from auto scrolling
     */
    autoscrollEnd() {
        window.clearInterval(this.autoScrollId);
        this.autoScrollId = null;

        const blur = this.nameSpacedEvent('blur');
        const focus = this.nameSpacedEvent('focus');
        const mouseout = this.nameSpacedEvent('mouseout');

        this.$windowEl.off(blur);
        this.$windowEl.off(focus).on(focus, () => this.autoscrollStart());
        this.$containerEl.off(mouseout).on(mouseout, () => this.autoscrollStart());
    }

    /**
     * Makes the scroller begin scrolling one item at a time
     */
    autoscrollStart() {
        this.autoscrollEnd(); // ensure we're not creating multiple event loops

        this.autoScrollId = window.setInterval(() => this.scrollForward(), this.slideDurationInMs);

        const blur = this.nameSpacedEvent('blur');
        const focus = this.nameSpacedEvent('focus');
        const mouseover = this.nameSpacedEvent('mouseover');

        this.$windowEl.off(blur).on(blur, () => this.autoscrollEnd());
        this.$windowEl.off(focus);

        this.$containerEl.off(mouseover).on(mouseover, () => this.autoscrollEnd());
    }

    /**
     * Builds clickable left and right controls that scroll the container forward and backward
     */
    buildControls() {
        this.controlContainerEl = document.createElement('div');
        this.controlContainerEl.classList.add('repeating-scroller-controls');

        const controlLeft = document.createElement('span');
        controlLeft.classList.add('cicon', 'cicon-chevron-left', 'js-scroll-left');
        controlLeft.dataset.value = 'previous';

        const controlRight = document.createElement('span');
        controlRight.classList.add('cicon', 'cicon-chevron-right', 'js-scroll-right');
        controlRight.dataset.value = 'next';

        if (this.controllerContainerWrapperEl) {
            this.controllerContainerWrapperEl.appendChild(this.controlContainerEl);
        }

        this.controlContainerEl.appendChild(controlLeft);
        this.controlContainerEl.appendChild(controlRight);
        this.containerEl.parentElement.insertBefore(
            this.controllerContainerWrapperEl || this.controlContainerEl,
            this.containerEl,
        );

        const click = this.nameSpacedEvent('click');
        const mouseout = this.nameSpacedEvent('mouseout');
        const mouseover = this.nameSpacedEvent('mouseover');

        const $controlLeft = $(controlLeft);
        $controlLeft.on(click, () => {
            this.scrollBackward(this.numVisibleElements);
            trackArrowClick({
                position: this.analyticsTrackingPosition,
                subtype: this.analyticsTrackingSubtype,
                type: this.analyticsTrackingType,
                value: $controlLeft.data('value'),
            });
        });

        const $controlRight = $(controlRight);
        $controlRight.on(click, () => {
            this.scrollForward(this.numVisibleElements);
            trackArrowClick({
                position: this.analyticsTrackingPosition,
                subtype: this.analyticsTrackingSubtype,
                type: this.analyticsTrackingType,
                value: $controlRight.data('value'),
            });
        });

        if (this.shouldAutoScroll) {
            $controlLeft.on(mouseout, () => this.autoscrollStart());
            $controlLeft.on(mouseover, () => this.autoscrollEnd());
            $controlRight.on(mouseout, () => this.autoscrollStart());
            $controlRight.on(mouseover, () => this.autoscrollEnd());
        }
    }

    /**
     * Given a calculated index that might be greater or less than the number of children (it is based on position),
     * returns the index of an actual child element
     *
     * @param {number} index The given index, which might be greater or less than the number of children
     * @returns {number} The normalized child index, which should always be present in `this.children`
     */
    getNormalizedIndex(index) {
        const childrenLength = this.children.length;
        let normalizedIndex = index;

        if (normalizedIndex >= childrenLength) {
            normalizedIndex %= childrenLength;
        }

        if (normalizedIndex < 0) {
            normalizedIndex += childrenLength;
        }

        return normalizedIndex;
    }

    /**
     * @param {string} eventName The name of the event to namespace
     * @returns {string} The namespaced event
     */
    nameSpacedEvent(eventName) {
        return [eventName, this.eventNameSpace].join('.');
    }

    /**
     * Positions all of the elements and surrounds index 0 with elements before and after it so it's
     * effectively in the "middle".
     */
    positionAllElements() {
        let i;
        this.isRepositioning = true; // ensures we don't reposition all elements again while this is in progress...

        for (i = 0; i < this.midpoint; i += 1) {
            this.positionChildEl(this.children[i], [this.originX + this.childWidth * i, 0]);
        }

        let count = 1;
        for (i = this.children.length - 1; i >= this.midpoint; i -= 1) {
            this.positionChildEl(this.children[i], [this.originX - this.childWidth * count, 0]);
            count += 1;
        }

        this.containerEl.scrollLeft = this.originX;
        this.currentScrollLeft = this.originX;
        this.isRepositioning = false;
    }

    /**
     * Transforms the element to the given coordinates
     *
     * @param {Element} el The element to position
     * @param {Array.<number>} coordinates The X and Y coordinates to set
     */
    positionChildEl(el, coordinates) {
        const element = el;
        if (!this.isInitialized) {
            element.style.left = 0; // ensure all elements start in the same place so the transform is correct
            element.style.position = 'absolute';
            element.style.willChange = 'transform';
        }

        element.style.transform = `translate(${coordinates[0]}px, ${coordinates[1]}px)`;
    }

    /**
     * Recalculates all of the necessary dimensions for the given screen size and re-positions everything
     */
    resizeAction() {
        const containerOffsetWidth = this.containerEl.offsetWidth;
        const numChildren = this.children.length;

        this.childWidth = Math.floor(this.children[0].offsetWidth); // assume that every element is the same width...

        this.childImgHeight = 0;

        if (this.childrenHaveImages) {
            this.childImgHeight = Math.floor($(this.children[0]).find('img')[0].offsetHeight);
        }

        this.numVisibleElements = Math.round(containerOffsetWidth / this.childWidth);

        if (numChildren <= this.numVisibleElements) {
            this.originX = 0;
        } else {
            this.originX = this.childWidth * numChildren;
        }

        this.$containerEl.css('overflow-x', 'scroll');

        this.midpoint =
            Math.floor(this.numVisibleElements * (Math.round(numChildren / this.numVisibleElements) - 1)) ||
            this.numVisibleElements;

        // TODO: (CHAIR-16614) Update when rolling out dynamic heights to all rails
        if (this.$containerEl.parent().hasClass('dynamic-height-rail')) {
            this.containerEl.style.height = RailUtils.calculateContainerHeight({ $children: $(this.children) });
        } else {
            this.containerEl.style.height = `${Math.floor(this.children[0].offsetHeight)}px`;
        }

        this.containerEl.style.position = 'relative';

        if (this.controlContainerEl) {
            if (numChildren <= this.numVisibleElements) {
                this.controlContainerEl.classList.add('hidden');
            } else {
                const chevronHeight = this.controlContainerEl.getElementsByClassName('cicon')[0].offsetHeight;
                this.controlContainerEl.style.width = `${this.childWidth}px`;
                this.controlContainerEl.style.margin = '0 auto';
                this.controlContainerEl.style.width = '100%';
                if (this.childImgHeight) {
                    this.controlContainerEl.style.marginTop = `${
                        Math.floor(this.childImgHeight / 2) - Math.floor(chevronHeight / 2)
                    }px`;
                }
                this.controlContainerEl.classList.remove('hidden');
            }
        }

        if (numChildren < this.minScrollCount) {
            this.$containerEl.css('overflow-x', 'hidden');
            if (this.controlContainerEl) {
                this.controlContainerEl.classList.add('hidden');
            }
        } else {
            this.positionAllElements();
        }
    }

    /**
     * The action to take on scroll to determine how and when to reposition which elements
     */
    scrollAction() {
        if (this.numVisibleElements < this.children.length) {
            const containerScrollLeft = this.containerEl.scrollLeft;
            const delta = containerScrollLeft - this.originX;

            const shouldReset = Math.abs(delta) >= this.children.length * this.childWidth;

            const isAdvancing = containerScrollLeft > this.currentScrollLeft;

            let index = Math.floor((this.originX + delta) / this.childWidth) - 1;
            index += isAdvancing ? this.midpoint : 0;

            if (shouldReset) {
                this.positionAllElements();
            } else if (delta !== 0 || !this.isRepositioning) {
                this.currentScrollLeft = containerScrollLeft;

                // calculate the "true" index based on scroll position
                index = Math.floor((this.originX + delta) / this.childWidth) - 1;
                index += isAdvancing ? this.midpoint : 0;

                // calculate the "actual" index based on the collection of children
                const normalizedIndex = this.getNormalizedIndex(index);
                const newXCoord = this.childWidth * index;
                const child = this.children[normalizedIndex];

                this.positionChildEl(child, [newXCoord, 0]);
            }
        }
    }

    /**
     * Scrolls the container by a given amount (in pixels)
     * Prevents overlapping scrolls by checking the isAnimating flag
     *
     * @param {number} scrollAmountInPixels the amount to scroll the container element (in pixels)
     */
    scrollContainer(scrollAmountInPixels) {
        if (this.isAnimating) {
            return;
        }
        this.isAnimating = true;
        this.$containerEl.stop().animate(
            {
                scrollLeft:
                    Math.floor(this.containerEl.scrollLeft / this.childWidth) * this.childWidth + scrollAmountInPixels,
            },
            this.transitionDurationInMs,
            () => {
                this.isAnimating = false;
            },
        );
    }

    /**
     * Static factory method to create a RepeatingScroller instance.
     *
     * Use {@link RepeatingScroller.createProductScroller} for product rails, or this method for custom options.
     *
     * @param {Object} options Options to pass to the RepeatingScroller constructor.
     * @returns {RepeatingScroller} The created instance.
     */
    static createWithOptions(options) {
        const instance = new RepeatingScroller(options);
        instance.bind();
        return instance;
    }

    /**
     * Creates a RepeatingScroller for a product rail container.
     *
     * This convenience method automatically sets the `container` and `children` options based on the provided jQuery element.
     *
     * @param {jQuery} $containerEl jQuery element for the container.
     * @param {Object=} options Additional options to pass (do not include `container` or `children`).
     * @returns {RepeatingScroller} The created instance.
     */
    static createProductScroller($containerEl, options = {}) {
        return RepeatingScroller.createWithOptions({
            ...options,
            container: $containerEl.get(0),
            children: $containerEl.children('.product').get(),
        });
    }
}

export default RepeatingScroller;
